import { cloneElement } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { SidebarTooltip } from './sidebar-tooltip'
import type { ComponentType, ReactElement, ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'

export const sidebarItemClassName = {
  base: 'flex shrink-0 items-center rounded-md text-sidebar-foreground/80 outline-none transition-colors hover:bg-sidebar-accent/70 hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
  expanded: 'h-8 w-full gap-2.5 px-2 text-sm',
  collapsed: 'mx-auto size-9 justify-center',
  active:
    'bg-sidebar-accent font-medium text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  icon: 'size-4 shrink-0',
} as const

interface SidebarNavItemProps {
  icon: ComponentType<{ className?: string }>
  label: string
  active?: boolean
  collapsed?: boolean
  /** Trailing content in the expanded row (badges, counters). */
  trailing?: ReactNode
  /** A `Link` or `button`; its own children are replaced by icon + label. */
  children: ReactElement
}

/**
 * One navigation row of the workspace sidebar. Expanded and collapsed share the
 * same element, sizes and active state; collapsed only drops the label into a
 * tooltip, so the two modes never drift apart.
 */
export function SidebarNavItem({
  icon: Icon,
  label,
  active = false,
  collapsed = false,
  trailing,
  children,
}: SidebarNavItemProps) {
  const item = (
    <Slot
      className={cn(
        sidebarItemClassName.base,
        collapsed
          ? sidebarItemClassName.collapsed
          : sidebarItemClassName.expanded,
        active && sidebarItemClassName.active,
      )}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? label : undefined}
    >
      {cloneElement(
        children,
        undefined,
        <>
          <Icon className={sidebarItemClassName.icon} aria-hidden="true" />
          {collapsed ? null : (
            <>
              <span className="min-w-0 flex-1 truncate text-left">{label}</span>
              {trailing}
            </>
          )}
        </>,
      )}
    </Slot>
  )

  return collapsed ? (
    <SidebarTooltip label={label}>{item}</SidebarTooltip>
  ) : (
    item
  )
}
